import { TFunction } from 'i18next'
import { useController } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'

import { defaultDropdownStyles } from '@/components/Clients/ClientProfileView/helpers'

import { TextField, ToggleField } from '../../legacy-form-fields'
import { SaltoSpaceConnectionFormValues } from './salto-space.schema'
import { useSaltoSpaceConnection } from './use-salto-space-connection'

interface SaltoSpaceFieldsProps {
  buildingId: number
  disabled: boolean
  isConfigured: boolean
}

const dropdownStyles = {
  ...defaultDropdownStyles(),
  menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 }),
}

interface AccessOption {
  saltoId: string
  name: string
}

interface AccessMultiSelectProps {
  name: 'residentZoneIds' | 'residentAccessLevelIds' | 'visitorZoneIds' | 'visitorAccessLevelIds'
  options: AccessOption[]
  placeholder: string
  disabled: boolean
  isConfigured: boolean
  isLoadingAccessPoints: boolean
}

const getEmptyOptionsMessage =
  (t: TFunction<['organisation']>, isLoading: boolean, configured: boolean) => () => {
    if (isLoading) return t('organisation:places.integrations.salto.emptyOptions.loading')
    if (configured) return t('organisation:places.integrations.salto.emptyOptions.syncing')
    return t('organisation:places.integrations.salto.emptyOptions.notConnected')
  }

const AccessMultiSelect = ({
  name,
  options,
  placeholder,
  disabled,
  isConfigured,
  isLoadingAccessPoints,
}: AccessMultiSelectProps) => {
  const { t } = useTranslation(['organisation'])
  const { field } = useController<SaltoSpaceConnectionFormValues>({ name })
  // Note: cast needed — `name` here is a union of the 4 array-typed field names (not a
  // literal at this call site), so RHF's path-value inference widens to the union of all
  // field types instead of narrowing to `string[]`; the 4 callers all pass one of the ids
  // fields, so this is always actually an array.
  const selectedIds = (field.value ?? []) as string[]
  const value = options.filter((option) => selectedIds.includes(option.saltoId))

  return (
    <Select
      isClearable
      isMulti
      closeMenuOnSelect={false}
      getOptionLabel={(option: AccessOption) => option.name}
      getOptionValue={(option: AccessOption) => option.saltoId}
      isDisabled={isLoadingAccessPoints || disabled}
      isLoading={isLoadingAccessPoints}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuPosition="fixed"
      noOptionsMessage={getEmptyOptionsMessage(t, isLoadingAccessPoints, isConfigured)}
      options={options}
      placeholder={placeholder}
      styles={dropdownStyles}
      value={value}
      onChange={(selected: readonly AccessOption[] | null) =>
        field.onChange((selected ?? []).map((o) => o.saltoId))
      }
    />
  )
}

export const SaltoSpaceFields = ({ buildingId, disabled, isConfigured }: SaltoSpaceFieldsProps) => {
  const { t } = useTranslation(['organisation'])
  const { zones, accessLevels, isLoadingAccessPoints, refetchAccessPoints } =
    useSaltoSpaceConnection(buildingId)
  const url = useController<SaltoSpaceConnectionFormValues>({ name: 'url' })
  const apiKey = useController<SaltoSpaceConnectionFormValues>({ name: 'apiKey' })
  const residentToggle = useController<SaltoSpaceConnectionFormValues>({
    name: 'shouldAutoAssignApartmentDoor',
  })
  const visitorToggle = useController<SaltoSpaceConnectionFormValues>({
    name: 'shouldAutoAssignUserAccessLevel',
  })

  return (
    <div className="integration-configuration">
      <TextField
        errorLabel={url.fieldState.error?.message}
        isError={Boolean(url.fieldState.error)}
        label={t('organisation:places.integrations.salto.urlField.label')}
        name={url.field.name}
        placeholder={t('organisation:places.integrations.salto.urlField.placeholder')}
        value={url.field.value ?? ''}
        onBlur={url.field.onBlur}
        onChange={url.field.onChange}
      />
      <TextField
        errorLabel={apiKey.fieldState.error?.message}
        isError={Boolean(apiKey.fieldState.error)}
        label={t('organisation:places.integrations.salto.apiKeyField.label')}
        name={apiKey.field.name}
        placeholder={t('organisation:places.integrations.salto.apiKeyField.placeholder')}
        type="password"
        value={apiKey.field.value ?? ''}
        onBlur={apiKey.field.onBlur}
        onChange={apiKey.field.onChange}
      />

      {isConfigured &&
        !isLoadingAccessPoints &&
        zones.length === 0 &&
        accessLevels.length === 0 && (
          <div className="integration-configuration__sync-hint">
            <p>{t('organisation:places.integrations.salto.syncHint')}</p>
            <button
              className="button button-transparent"
              type="button"
              onClick={() => void refetchAccessPoints()}
            >
              {t('organisation:places.integrations.salto.refreshData')}
            </button>
          </div>
        )}

      <div className="access-container">
        <h3 className="access-title">
          {t('organisation:places.integrations.salto.residentAccesses')}
        </h3>
        <ToggleField
          id="shouldAutoAssignApartmentDoor"
          label={t('organisation:places.integrations.salto.autoAssignResidentDoorAccess')}
          name={residentToggle.field.name}
          value={residentToggle.field.value}
          onChange={residentToggle.field.onChange}
        />
        <div className="access-row">
          <div className="access-column">
            <div className="access-label">
              {t('organisation:places.integrations.salto.zone.label')}
            </div>
            <AccessMultiSelect
              disabled={disabled}
              isConfigured={isConfigured}
              isLoadingAccessPoints={isLoadingAccessPoints}
              name="residentZoneIds"
              options={zones}
              placeholder={t('organisation:places.integrations.salto.zone.placeholder')}
            />
          </div>
          <div className="access-column">
            <div className="access-label">
              {t('organisation:places.integrations.salto.accessLevel.label')}
            </div>
            <AccessMultiSelect
              disabled={disabled}
              isConfigured={isConfigured}
              isLoadingAccessPoints={isLoadingAccessPoints}
              name="residentAccessLevelIds"
              options={accessLevels}
              placeholder={t('organisation:places.integrations.salto.accessLevel.placeholder')}
            />
          </div>
        </div>
      </div>

      <div className="access-container">
        <h3 className="access-title">
          {t('organisation:places.integrations.salto.visitorAccesses')}
        </h3>
        <ToggleField
          id="shouldAutoAssignUserAccessLevel"
          label={t('organisation:places.integrations.salto.autoAssignVisitorDoorAccess')}
          name={visitorToggle.field.name}
          value={visitorToggle.field.value}
          onChange={visitorToggle.field.onChange}
        />
        <div className="access-row">
          <div className="access-column">
            <div className="access-label">
              {t('organisation:places.integrations.salto.zone.label')}
            </div>
            <AccessMultiSelect
              disabled={disabled}
              isConfigured={isConfigured}
              isLoadingAccessPoints={isLoadingAccessPoints}
              name="visitorZoneIds"
              options={zones}
              placeholder={t('organisation:places.integrations.salto.zone.placeholder')}
            />
          </div>
          <div className="access-column">
            <div className="access-label">
              {t('organisation:places.integrations.salto.accessLevel.label')}
            </div>
            <AccessMultiSelect
              disabled={disabled}
              isConfigured={isConfigured}
              isLoadingAccessPoints={isLoadingAccessPoints}
              name="visitorAccessLevelIds"
              options={accessLevels}
              placeholder={t('organisation:places.integrations.salto.accessLevel.placeholder')}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
